/**
 * ModuleVisibilityManager - Granular control to show/hide modules publicly
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';

// Icons
import {
  Calendar, Church, Heart, BookOpen, Target,
  Eye, EyeOff, Navigation, LayoutGrid, Footprints,
  AlertTriangle, Save, Loader2, RefreshCw, Settings2,
  GraduationCap, Camera, Wrench, Home as HomeIcon, Users,
} from 'lucide-react';

const MODULE_CONFIG = {
  events: {
    icon: Calendar,
    name: 'Events',
    description: 'Event listing, registration & tickets',
    color: '#3B82F6'
  },
  churches: {
    icon: Church,
    name: 'Churches',
    description: 'Church directory, CMS & live streaming',
    color: '#14B8A6'
  },
  schools: {
    icon: GraduationCap,
    name: 'Schools',
    description: 'School/Bible college admissions & portal',
    color: '#F59E0B'
  },
  giving: {
    icon: Heart,
    name: 'Giving',
    description: 'Online giving and tithes',
    color: '#22C55E'
  },
  bible: {
    icon: BookOpen,
    name: 'Bible',
    description: 'Bible reading and devotionals',
    color: '#6366F1'
  },
  campaigns: {
    icon: Target,
    name: 'Campaigns',
    description: 'Fundraising campaigns & pledges',
    color: '#8B5CF6'
  },
  studio: {
    icon: Camera,
    name: 'Studio',
    description: 'Public studio booking marketplace',
    color: '#EC4899'
  },
  housing: {
    icon: HomeIcon,
    name: 'Housing',
    description: 'Public accommodation bookings',
    color: '#0EA5E9'
  },
  facilities: {
    icon: Wrench,
    name: 'Facilities',
    description: 'Public report-issue forms',
    color: '#6366F1'
  },
  outreach: {
    icon: Users,
    name: 'Outreach',
    description: 'Outreach centres & community',
    color: '#F97316'
  }
};

const ModuleVisibilityCard = ({ moduleId, config, settings, onUpdate }) => {
  const ModuleIcon = config.icon;
  const [localSettings, setLocalSettings] = React.useState(settings);
  const [hasChanges, setHasChanges] = React.useState(false);

  React.useEffect(() => {
    setLocalSettings(settings);
    setHasChanges(false);
  }, [settings]);

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(moduleId, localSettings);
    setHasChanges(false);
  };

  const isVisible = localSettings?.visible;
  const isInMaintenance = localSettings?.maintenance_mode;

  return (
    <Card className={`${!isVisible && 'opacity-60 border-dashed'} ${isInMaintenance && 'border-amber-300 bg-amber-50/30'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div 
              className={`p-2.5 rounded-lg ${isVisible ? '' : 'bg-slate-100'}`}
              style={{ backgroundColor: isVisible ? `${config.color}15` : undefined }}
            >
              <ModuleIcon 
                className="h-5 w-5"
                style={{ color: isVisible ? config.color : '#9CA3AF' }}
              />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {config.name}
                {!isVisible && <Badge variant="secondary" className="text-xs">Hidden</Badge>}
                {isInMaintenance && <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Maintenance</Badge>}
              </CardTitle>
              <CardDescription className="text-xs">{config.description}</CardDescription>
            </div>
          </div>
          {hasChanges && (
            <Button size="sm" onClick={handleSave} className="h-7">
              <Save className="h-3 w-3 mr-1" />
              Save
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Master Visibility Toggle */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-2">
            {isVisible ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
            <Label className="font-medium">
              {isVisible ? 'Publicly Visible' : 'Hidden from Public'}
            </Label>
          </div>
          <Switch
            checked={isVisible}
            onCheckedChange={(checked) => handleChange('visible', checked)}
          />
        </div>

        {isVisible && (
          <>
            <Separator />
            
            {/* Granular Controls */}
            <div className="space-y-3">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                Display Locations
              </Label>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="flex flex-col items-center p-3 border rounded-lg">
                  <Navigation className="h-4 w-4 mb-2 text-slate-500" />
                  <span className="text-xs text-center mb-2">Navigation</span>
                  <Switch
                    checked={localSettings?.show_in_nav}
                    onCheckedChange={(checked) => handleChange('show_in_nav', checked)}
                  />
                </div>
                
                <div className="flex flex-col items-center p-3 border rounded-lg">
                  <LayoutGrid className="h-4 w-4 mb-2 text-slate-500" />
                  <span className="text-xs text-center mb-2">Quick Access</span>
                  <Switch
                    checked={localSettings?.show_in_quick_access}
                    onCheckedChange={(checked) => handleChange('show_in_quick_access', checked)}
                  />
                </div>
                
                <div className="flex flex-col items-center p-3 border rounded-lg">
                  <Footprints className="h-4 w-4 mb-2 text-slate-500" />
                  <span className="text-xs text-center mb-2">Footer</span>
                  <Switch
                    checked={localSettings?.show_in_footer}
                    onCheckedChange={(checked) => handleChange('show_in_footer', checked)}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Maintenance Mode */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <Label>Maintenance Mode</Label>
                </div>
                <Switch
                  checked={localSettings?.maintenance_mode}
                  onCheckedChange={(checked) => handleChange('maintenance_mode', checked)}
                />
              </div>
              
              {localSettings?.maintenance_mode && (
                <Input
                  value={localSettings?.maintenance_message || ''}
                  onChange={(e) => handleChange('maintenance_message', e.target.value)}
                  placeholder="Maintenance message to display..."
                  className="text-sm"
                />
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const ModuleVisibilityManager = () => {
  const queryClient = useQueryClient();

  // Fetch current visibility settings
  const { data: visibility, isLoading } = useQuery({
    queryKey: ['module-visibility'],
    queryFn: () => api.get('/module-visibility').then(r => r.data)
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ moduleId, settings }) => 
      api.put(`/admin/module-visibility/${moduleId}`, null, { params: settings }),
    onSuccess: (_, { moduleId }) => {
      toast.success(`${MODULE_CONFIG[moduleId]?.name || moduleId} visibility updated`);
      queryClient.invalidateQueries(['module-visibility']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to update visibility');
    }
  });

  // Bulk hide all mutation
  const hideAllMutation = useMutation({
    mutationFn: () => {
      const settings = {};
      Object.keys(MODULE_CONFIG).forEach(moduleId => {
        settings[moduleId] = { visible: false };
      });
      return api.put('/admin/module-visibility/bulk', settings);
    },
    onSuccess: () => {
      toast.success('All modules hidden');
      queryClient.invalidateQueries(['module-visibility']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to hide modules');
    }
  });

  // Bulk show all mutation
  const showAllMutation = useMutation({
    mutationFn: () => {
      const settings = {};
      Object.keys(MODULE_CONFIG).forEach(moduleId => {
        settings[moduleId] = { visible: true, show_in_nav: true, show_in_quick_access: true, show_in_footer: true };
      });
      return api.put('/admin/module-visibility/bulk', settings);
    },
    onSuccess: () => {
      toast.success('All modules visible');
      queryClient.invalidateQueries(['module-visibility']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to show modules');
    }
  });

  const handleUpdate = (moduleId, settings) => {
    updateMutation.mutate({ moduleId, settings });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  const visibleCount = Object.values(visibility || {}).filter(v => v.visible).length;
  const maintenanceCount = Object.values(visibility || {}).filter(v => v.maintenance_mode).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-primary" />
            Module Visibility
          </h2>
          <p className="text-sm text-muted-foreground">
            Control which modules are visible to the public. Hide modules that are under development or not ready.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <Eye className="h-3 w-3" />
            {visibleCount} visible
          </Badge>
          {maintenanceCount > 0 && (
            <Badge variant="outline" className="gap-1 text-amber-600 border-amber-300">
              <AlertTriangle className="h-3 w-3" />
              {maintenanceCount} maintenance
            </Badge>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <Alert>
        <AlertDescription className="flex items-center justify-between">
          <span>
            Quickly show or hide all modules at once for maintenance or launch preparation.
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => hideAllMutation.mutate()}
              disabled={hideAllMutation.isPending}
            >
              {hideAllMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
              Hide All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => showAllMutation.mutate()}
              disabled={showAllMutation.isPending}
            >
              {showAllMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              Show All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => queryClient.invalidateQueries(['module-visibility'])}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      {/* Module Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(MODULE_CONFIG).map(([moduleId, config]) => (
          <ModuleVisibilityCard
            key={moduleId}
            moduleId={moduleId}
            config={config}
            settings={visibility?.[moduleId]}
            onUpdate={handleUpdate}
          />
        ))}
      </div>

      {/* Info Note */}
      <Alert className="bg-blue-50 border-blue-200">
        <AlertDescription className="text-blue-800">
          <strong>Tip:</strong> Use "Maintenance Mode" to show a message instead of completely hiding a module. 
          This is useful when you're fixing bugs but want users to know the feature exists.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default ModuleVisibilityManager;
